import type { MedplumClient } from '@medplum/core';
import type { ExtractResource, ResourceType } from '@medplum/fhirtypes';
import { useEffect, useMemo, useState } from 'react';

import { useMedplum } from '@eluve/feature-patients';

/**
 * REST-side counterparts to useFhirData/useFhirConnection: the same
 * query-state shape for Medplum SDK operations that have no GraphQL surface
 * (`_history`, `Patient/$summary`, typeahead searches). One state machine —
 * client gating, cancellation, refetch — instead of a hand-rolled
 * useEffect + cancelled flag per hook.
 */
export type FhirRestReadState<T> = {
  /** Result of the read; null until loaded (or while skipped). */
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  /** False while the Medplum client hasn't initialized (feature off, session pending). */
  enabled: boolean;
};

/**
 * Run a Medplum SDK read with shared query-state semantics.
 *
 * - `read` is invoked with the authenticated client; pass `null` to skip
 *   (missing id, query too short) — state stays empty, `loading` false.
 * - `deps` are the values `read` closes over — the read re-runs when they
 *   change, exactly like a useEffect dependency array.
 * - Stale responses are dropped (cancelled flag), never raced into state.
 *
 * @example
 * const { data: bundle } = useFhirRestRead(
 *   fhirPatientId
 *     ? (client) => client.get(client.fhirUrl('Patient', fhirPatientId, '$summary'), { cache: 'no-cache' })
 *     : null,
 *   [fhirPatientId],
 * );
 */
export function useFhirRestRead<T>(
  read: ((client: MedplumClient) => Promise<T>) | null,
  deps: readonly unknown[],
): FhirRestReadState<T> {
  const { client } = useMedplum();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [epoch, setEpoch] = useState(0);

  const skipped = !client || !read;
  useEffect(() => {
    if (skipped) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    read(client)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setData(null);
        setError(caught instanceof Error ? caught : new Error(String(caught)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `read` is a fresh closure every render — `deps` are its actual inputs.
  }, [client, skipped, epoch, ...deps]);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      refetch: () => setEpoch((current) => current + 1),
      enabled: Boolean(client),
    }),
    [data, loading, error, client],
  );
}

const EMPTY_RESOURCES: never[] = [];

/**
 * Typeahead-friendly resource search over the SDK. Pass `null` params to
 * skip (query too short, dialog closed) — results clear without a request.
 * No debounce: the Medplum client dedupes/caches identical searches, which
 * is acceptable at dialog-typeahead scale.
 *
 * @example
 * const { resources: patients } = useFhirSearch(
 *   'Patient',
 *   query.trim().length >= 2 ? { name: query.trim(), _count: '8', _sort: 'family' } : null,
 * );
 */
export function useFhirSearch<K extends ResourceType>(
  resourceType: K,
  params: Record<string, string> | null,
): {
  resources: ExtractResource<K>[];
  loading: boolean;
  error: Error | null;
  enabled: boolean;
} {
  // Key the effect on the serialized params, not object identity — callers
  // pass a fresh object literal every render.
  const paramsKey = params ? JSON.stringify(params) : null;
  const { data, loading, error, enabled } = useFhirRestRead(
    params
      ? async (client) => [
          ...(await client.searchResources(resourceType, params)),
        ]
      : null,
    [resourceType, paramsKey],
  );
  return { resources: data ?? EMPTY_RESOURCES, loading, error, enabled };
}
